import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router';

import messages from 'clusters/displayMessages';
import { ItemsApi } from 'global/api';
import { Cluster } from 'labxchange-client';
import { TranslationSelectorModal } from 'library/components';
import libraryMessages, {
    getBackgroundKnowledgeLabel,
    getContentLicenseLabel,
} from 'library/displayMessages';
import { SubjectTags } from 'library/components/ItemMetadataTable';

import { detailUrlForEntity } from 'library/utils';
import { Icon, showErrorMessage } from 'ui/components';
import { getLanguageNativeName } from 'i18n';
import { WrappedMessage } from 'utils';
import { LanguageItem } from 'library/components/TranslationSelectorModal/TranslationSelectorModal';

// Only show views if more than this number.
const ViewsDisplayThreshold = 1000;

interface ItemActionsProps extends RouteComponentProps<{}> {
    cluster: Cluster;
}

interface ItemActionsState {
    showLanguageSwitcherModal: boolean;
    showDetails: boolean;
    translations: LanguageItem[];
}

class ClusterMetaInternal extends React.PureComponent<ItemActionsProps, ItemActionsState> {

    constructor(props: ItemActionsProps) {
        super(props);
        this.state = {
            showLanguageSwitcherModal: false,
            showDetails: false,
            translations: [],
        };
    }

    async componentDidMount() {
        try {
            const translations = await ItemsApi.languages({id: this.props.cluster.metadata.metadata.id});
            this.setState({ translations });
        } catch (error) {
            showErrorMessage(<WrappedMessage message={messages.loadingPathwayTranslationsError}/>);
        }
    }

    public async componentDidUpdate(prevProps: ItemActionsProps, prevState: ItemActionsState) {
        if (this.props.cluster.id !== prevProps.cluster.id) {
            this.setState({ showLanguageSwitcherModal: false });
        }
    }

    public render() {
        const { metadata } = this.props.cluster!.metadata;
        const languageCode = metadata.language;
        const languageName = getLanguageNativeName(languageCode);

        const toggleIcon = this.state.showDetails ? 'chevron-up' : 'chevron-down';
        const toggleMessage = this.state.showDetails ? messages.clusterHideDetails : messages.clusterShowDetails;
        return (
            <>
                <button
                    onClick={() => this.setState({showDetails: !this.state.showDetails})}
                    className='lx-btn hide-on-tablet-and-above show-details'
                >
                    <WrappedMessage message={toggleMessage}/>
                    <Icon name={toggleIcon} />
                </button>
                <ul className={`meta-information row ${!this.state.showDetails ? 'lxc-hide-mobile' : ''}`}>
                    <li className='col-lg-2 col-md-4 col-sm-12'>
                        <span className='key'>
                            <Icon name='star' /><WrappedMessage message={libraryMessages.metadataFavorites}/>
                        </span>
                        <span className='value'>
                            {metadata.stats.favorites}
                        </span>
                    </li>

                    {metadata.stats.views > ViewsDisplayThreshold &&
                        <li className='col-lg-2 col-md-4 col-sm-12'>
                            <span className='key'>
                                <Icon name='eye'/><WrappedMessage message={libraryMessages.metadataViews}/>
                            </span>
                            <span className='value'>
                                {metadata.stats.views}
                            </span>
                        </li>
                    }
                    <li className='col-lg-2 col-md-4 col-sm-12'>
                        <span className='key'>
                            <Icon name='subject' /><WrappedMessage message={libraryMessages.metadataSubject}/>
                        </span>
                        <span className='value'>
                            <SubjectTags subjectTags={metadata.subjectTags} />
                        </span>
                    </li>
                    <li className='col-lg-2 col-md-4 col-sm-12'>
                        <span className='key'>
                            <Icon name='language' /><WrappedMessage message={libraryMessages.metadataLanguage}/>
                        </span>
                        <span className='value'>
                            {languageName}
                            {this.state.translations.length > 1 ?
                                this.languageSwitcherController() : null
                            }
                        </span>
                    </li>
                    <li className='col-lg-2 col-md-4 col-sm-12'>
                        <span className='key'>
                            <Icon name='background-knowledge' />
                            <WrappedMessage message={libraryMessages.metadataBackgroundKnowledge}/>
                        </span>
                        <span className='value'>
                            <WrappedMessage message={getBackgroundKnowledgeLabel(metadata.backgroundKnowledge)}/>
                        </span>
                    </li>
                    <li className='col-lg-2 col-md-4 col-sm-12'>
                        <span className='key'>
                            <Icon name='license' /><WrappedMessage message={libraryMessages.metadataLicense}/>
                        </span>
                        <span className='value'>
                            <WrappedMessage message={getContentLicenseLabel(metadata.license)} />
                        </span>
                    </li>
                    {this.languageSwitcher()}
                </ul>
            </>
        );
    }

    private languageSwitcherController() {
        return (
            <button
                className='language-switcher'
                onClick={() => this.setState({showLanguageSwitcherModal: true})}
            >
                (<WrappedMessage message={messages.clusterLanguageChange} />)
            </button>
        );
    }

    private languageSwitcher() {
        const { metadata } = this.props.cluster!.metadata;
        return (
            this.state.showLanguageSwitcherModal && <TranslationSelectorModal
                translations={this.state.translations}
                metadata={metadata}
                onClose={() => this.setState({showLanguageSwitcherModal: false})}
                onConfirm={(translation) => {
                    this.props.history.push(detailUrlForEntity(
                        {id: translation.item, type: translation.type},
                    ));
                }}
            />
        );
    }

}

export const ClusterMeta = withRouter(ClusterMetaInternal);
